import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { clearCredentials } from '../store/slices/authSlice';
import { getVaultMemberMePending, acceptVaultMemberInvite, rejectVaultMemberInvite } from '../store/slices/vaultMemberSlice';
import ThemeSelector from '../components/ThemeSelector';
import ChangePasswordModal from '../components/ChangePasswordModal';
import MobileMenu from './MobileMenu';

interface HeaderProps {
  title?: string;
  subtitle?: string;
}

const Header: React.FC<HeaderProps> = ({
  title = 'Dashboard',
  subtitle = "Welcome back! Here's your security overview.",
}) => {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const mePendingVaults = useAppSelector((state) => state.vaultMember.mePendingVaults);
  const mePendingLoading = useAppSelector((state) => state.vaultMember.operationLoading.mePending);
  const mePendingError = useAppSelector((state) => state.vaultMember.errors.mePending);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const toggleLanguage = () => {
    changeLanguage(currentLanguage === 'tr' ? 'en' : 'tr');
  };

  const handleLogout = () => {
    dispatch(clearCredentials());
    navigate('/login');
    setIsDropdownOpen(false);
  };

  const handleChangePasswordClick = () => {
    setIsDropdownOpen(false);
    setIsChangePasswordModalOpen(true);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsDropdownOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(target)) {
        setIsNotificationsOpen(false);
      }
    };

    if (isDropdownOpen || isNotificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isNotificationsOpen]);

  const handleNotificationsClick = () => {
    const next = !isNotificationsOpen;
    setIsNotificationsOpen(next);
    if (next) dispatch(getVaultMemberMePending());
  };

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-3 lg:px-6 py-2 lg:py-3 h-[60px] lg:h-[75px]">
      <div className="flex items-center justify-between h-full">
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(true)}
          className="lg:hidden p-2 mr-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          aria-label="Menu"
        >
          <i className="fa-solid fa-bars text-lg" />
        </button>
        <Link
          to="/workspace"
          className="flex items-center gap-0 cursor-pointer hover:opacity-80 transition-opacity"
        >
          <img
            src="/darkLogo.png"
            alt="Logo"
            className="h-10 lg:h-16 object-contain"
          />
          <span className="text-xl lg:text-3xl font-medium text-gray-500 dark:text-gray-300 font-maven">SmartPM</span>
        </Link>

        {/* Center Title */}
        <div className="flex-1 justify-center hidden md:flex">
          <h1 className="text-lg lg:text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h1>
        </div>

        <div className="hidden lg:flex items-center space-x-4">
          {/* Notifications — pending vault invites */}
          <div className="relative" ref={notificationsRef}>
            <button
              type="button"
              onClick={handleNotificationsClick}
              className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors relative"
              aria-label={t('header.notifications')}
              title={t('header.notifications')}
              aria-expanded={isNotificationsOpen}
            >
              <i className="fa-solid fa-bell text-lg" />
              {mePendingVaults.length > 0 && (
                <span className="absolute top-1 right-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-white">
                  {mePendingVaults.length > 9 ? '9+' : mePendingVaults.length}
                </span>
              )}
            </button>
            {isNotificationsOpen && (
              <div className="absolute right-0 mt-2 w-72 max-h-[min(24rem,70vh)] overflow-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700/80">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {t('header.pendingInvites')}
                  </h3>
                </div>
                <div className="py-2">
                  {mePendingLoading ? (
                    <div className="px-4 py-6 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
                      <i className="fa-solid fa-spinner fa-spin" />
                      {t('common.loading')}
                    </div>
                  ) : mePendingError ? (
                    <p className="px-4 py-3 text-sm text-red-600 dark:text-red-400" role="alert">
                      {mePendingError}
                    </p>
                  ) : mePendingVaults.length === 0 ? (
                    <p className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                      {t('header.noPendingInvites')}
                    </p>
                  ) : (
                    <ul className="divide-y divide-gray-100 dark:divide-gray-700/80">
                      {mePendingVaults.map((item) => (
                        <li key={item.vaultId} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {item.vaultName ?? '—'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {t('header.vaultInviteMessage')}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => dispatch(acceptVaultMemberInvite(item.vaultId))}
                                className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-500/10 dark:hover:bg-green-500/20 rounded-lg transition-colors"
                                title={t('header.acceptInvite')}
                                aria-label={t('header.acceptInvite')}
                              >
                                <i className="fa-solid fa-check text-sm" />
                              </button>
                              <button
                                type="button"
                                onClick={() => dispatch(rejectVaultMemberInvite(item.vaultId))}
                                className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-500/10 dark:hover:bg-red-500/20 rounded-lg transition-colors"
                                title={t('header.rejectInvite')}
                                aria-label={t('header.rejectInvite')}
                              >
                                <i className="fa-solid fa-times text-sm" />
                              </button>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Theme Selector */}
          <ThemeSelector />

          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
            aria-label="Toggle language"
            title={currentLanguage === 'tr' ? t('header.switchToEnglish') : t('header.switchToTurkish')}
          >
            {currentLanguage === 'tr' ? (
              <>
                <img src="https://flagcdn.com/w40/tr.png" alt="" className="w-5 h-[15px] object-cover rounded-sm shadow-sm" aria-hidden />
                TR
              </>
            ) : (
              <>
                <img src="https://flagcdn.com/w40/us.png" alt="" className="w-5 h-[15px] object-cover rounded-sm shadow-sm" aria-hidden />
                EN
              </>
            )}
          </button>

          {/* User Profile */}
          <div className="relative" ref={dropdownRef}>
            <div 
              onClick={toggleDropdown}
              className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                <i className="fa-solid fa-user text-gray-600 dark:text-gray-300 text-xs"></i>
              </div>
              <div className="text-sm">
                <p className="font-medium text-gray-800 dark:text-gray-200">{user?.name ?? user?.email ?? 'User'}</p>
              </div>
              <i className={`fa-solid fa-chevron-down text-gray-400 dark:text-gray-500 text-xs transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
            </div>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                <button
                  type="button"
                  onClick={handleChangePasswordClick}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center space-x-2"
                >
                  <i className="fa-solid fa-key text-gray-500 dark:text-gray-400" />
                  <span>{t('header.changePassword')}</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center space-x-2"
                >
                  <i className="fa-solid fa-sign-out-alt text-gray-500 dark:text-gray-400"></i>
                  <span>{t('header.logout')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)}>
        <ThemeSelector />
        <button
          onClick={toggleLanguage}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <img
            src={currentLanguage === 'tr' ? 'https://flagcdn.com/w40/tr.png' : 'https://flagcdn.com/w40/us.png'}
            alt=""
            className="w-5 h-[15px] object-cover rounded-sm"
            aria-hidden
          />
          {currentLanguage === 'tr' ? 'Türkçe' : 'English'}
        </button>
        <div className="px-3 py-2 text-sm text-gray-800 dark:text-gray-200 font-medium truncate">
          {user?.name ?? user?.email ?? 'User'}
        </div>
        <button
          onClick={() => { setIsMobileMenuOpen(false); handleChangePasswordClick(); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <i className="fa-solid fa-key text-gray-500 dark:text-gray-400 w-5 text-center" />
          <span>{t('header.changePassword')}</span>
        </button>
        <button
          onClick={() => { setIsMobileMenuOpen(false); handleLogout(); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <i className="fa-solid fa-sign-out-alt text-gray-500 dark:text-gray-400 w-5 text-center" />
          <span>{t('header.logout')}</span>
        </button>
      </MobileMenu>
    </header>
  );
};

export default Header;

